// ui/data/queryClient.js — the ONE react-query client of the app.
//
// Pages used to fetch in useEffect on mount and throw the data away on
// unmount, so every route change showed spinners and re-read everything.
// Now the redesigned pages read through the hooks in ./queries.js, which keep
// their data in this client's in-memory cache (stale-while-revalidate): a
// revisit renders the cached copy at once and refreshes it in the background.
//
//   staleTime 30 s     a revisit within 30 s does not refetch at all; money
//                      reads (halt state, positions, balances, risk limits)
//                      override this with 0 — cached values render first,
//                      but they are ALWAYS re-read on mount.
//   gcTime 30 min      data nobody is looking at is dropped after 30 min.
//   refetchOnWindowFocus, structural sharing (unchanged rows keep identity).
//   retry              once, and never on a 4xx (a 404 means "this api build
//                      has no such endpoint", a 401 is handled by http.js).
//   polls              pause while the tab is hidden (refetchIntervalInBackground
//                      false), and only ONE observer per key sets the interval.
//
// In-memory only: a full page reload starts empty.
//
// SECURITY: the cache holds one user's data. `resetQueryCache()` wipes it (and
// cancels in-flight reads, so a late response of user A never lands in user
// B's cache). It is called on every session change: jwtAuthService.setSession
// (login, logout, expiry, the 401 interceptor, SessionWatcher), logoutFlow,
// the direct token writes of the login/sign-up screens, and a `storage` event
// that changes the token in another tab.
//
// React code gets the client with `useAppQueryClient()` (the provider's, or
// this singleton when rendered without one — e.g. a component test), never by
// importing `appQueryClient` directly, so tests can inject a fresh client.
import { useContext } from 'react';
import { QueryClient, QueryClientContext } from '@tanstack/react-query';

const IS_TEST = process.env.NODE_ENV === 'test';

export const STALE_MS = 30 * 1000;
export const GC_MS = 30 * 60 * 1000;

/** Retry once, never on a 4xx. */
export const retryPolicy = (failures, err) => {
  const status = err && err.response && err.response.status;
  if (status >= 400 && status < 500) return false;
  return failures < 1;
};

export const DEFAULT_QUERY_OPTIONS = {
  staleTime: STALE_MS,
  gcTime: GC_MS,
  refetchOnWindowFocus: true,
  refetchIntervalInBackground: false,
  structuralSharing: true,
  // Tests assert on first-failure states; a retry would add a 1 s delay.
  retry: IS_TEST ? false : retryPolicy,
};

export function createAppQueryClient(overrides = {}) {
  return new QueryClient({
    defaultOptions: {
      queries: { ...DEFAULT_QUERY_OPTIONS, ...(overrides.queries || {}) },
      mutations: { retry: false, ...(overrides.mutations || {}) },
    },
  });
}

export const appQueryClient = createAppQueryClient();

/** The provider's client, or the app singleton outside a provider. */
export function useAppQueryClient() {
  const ctx = useContext(QueryClientContext);
  return ctx || appQueryClient;
}

/**
 * Wipe every cached read (and cancel in-flight ones). Safe to call any number
 * of times; `client` defaults to the app singleton.
 */
export function resetQueryCache(client = appQueryClient) {
  // clear() destroys every query, and Query.destroy() cancels its fetch
  // silently, so an in-flight response is dropped rather than cached.
  client.clear();
}

/**
 * Another tab signed in as someone else / signed out: the token under our feet
 * changed, so nothing cached here belongs to the current session any more.
 * Wipe the cache, then leave the signed-in pages: a full reload of #/sign-in
 * (or of the app, for a new session) drops the old user's mounted pages and
 * their socket too. Only on /profile routes — a public page has nothing to drop.
 * Exported for the test.
 */
export function onStorageChange(e, { navigate = (hash) => { window.location.hash = hash; window.location.reload(); } } = {}) {
  if (!e) return false;
  // key null = localStorage.clear() (a sign-out in the other tab).
  if (!(e.key === null || (e.key === 'token' && e.oldValue !== e.newValue))) return false;
  resetQueryCache();
  const hash = (typeof window !== 'undefined' && window.location && window.location.hash) || '';
  if (hash.startsWith('#/profile')) {
    const signedOut = e.key === null || !e.newValue;
    navigate(signedOut ? '#/sign-in' : '#/profile/portfolio');
  }
  return true;
}

if (typeof window !== 'undefined' && window.addEventListener) {
  window.addEventListener('storage', (e) => onStorageChange(e));
}
